import { supabase } from "../integrations/supabase/client";

export type SubscriptionTier = "Registered" | "Basic" | "Pro" | "ProPlus";

export type SubscriptionResponse = {
    success: boolean;
    message: string;
    url?: string;
    error?: any;
};

class SubscriptionService {
    private static instance: SubscriptionService;

    private constructor() {}

    static getInstance(): SubscriptionService {
        if (!SubscriptionService.instance) {
            SubscriptionService.instance = new SubscriptionService();
        }
        return SubscriptionService.instance;
    }

    async createSubscription(
        user_id: string,
        email: string,
        tier_code: SubscriptionTier,
    ): Promise<SubscriptionResponse> {
        try {
            const { data, error } = await supabase.functions.invoke(
                "create-subscription",
                {
                    method: "POST",
                    body: { user_id, tier_code, email },
                },
            );

            if (error) {
                console.error("Error creating subscription:", error);
                return {
                    success: false,
                    message: "Failed to create subscription",
                    error,
                };
            }

            //console.log("create subscription data is", data);

            return {
                success: true,
                message: "Subscription created successfully",
                url: data?.url,
            };
        } catch (error) {
            console.error("Error creating subscription:", error);
            return {
                success: false,
                message: "An unexpected error occurred",
                error,
            };
        }
    }

    async createCustomerPortalSession(
        user_id: string,
        returnUrl: string,
    ): Promise<SubscriptionResponse> {
        try {
            const { data, error } = await supabase.functions.invoke(
                "customer-portal-session",
                {
                    method: "POST",
                    body: { userId: user_id, returnUrl },
                },
            );

            if (error) {
                console.error("Error creating customer portal session:", error);
                return {
                    success: false,
                    message: "Failed to create customer portal session",
                    error,
                };
            }

            console.log("create customer portal session data is", data);

            return {
                success: true,
                message: "Customer portal session created successfully",
                url: data?.url,
            };
        } catch (error) {
            console.error("Error creating customer portal session:", error);
            return {
                success: false,
                message: "An unexpected error occurred",
                error,
            };
        }
    }

    async changeSubscription(
        user_id: string,
        tier_code: SubscriptionTier,
    ): Promise<SubscriptionResponse> {
        try {
            const { data, error } = await supabase.functions.invoke(
                "change-subscription",
                {
                    method: "PUT",
                    body: { user_id, tier_code },
                },
            );

            if (error) {
                console.error("Error changing subscription:", error);
                return {
                    success: false,
                    message: "Failed to change subscription",
                    error,
                };
            }

            return {
                success: true,
                message: "Subscription changed successfully",
                url: data?.url,
            };
        } catch (error) {
            console.error("Error changing subscription:", error);
            return {
                success: false,
                message: "An unexpected error occurred",
                error,
            };
        }
    }

    async cancelSubscription(user_id: string): Promise<SubscriptionResponse> {
        try {
            const { data, error } = await supabase.functions.invoke(
                "cancel-subscription",
                { method: "PUT", body: { user_id } },
            );

            if (error) {
                console.error("Error cancelling subscription:", error);
                return {
                    success: false,
                    message: "Failed to cancel subscription",
                    error,
                };
            }

            return {
                success: true,
                message: "Subscription cancelled successfully",
            };
        } catch (error) {
            console.error("Error cancelling subscription:", error);
            return {
                success: false,
                message: "An unexpected error occurred",
                error,
            };
        }
    }

    async getUserSubscriptionStatus(user_id: string): Promise<{
        isSubscribed: boolean;
        tier_code?: SubscriptionTier;
        subscription_id?: string;
        customer_id?: string;
        marked_for_cancel?: boolean;
        cancel_at?: Date;
    }> {
        try {
            console.log("user_id is", user_id);

            const { data: user_subscription, error: user_subscription_error } =
                await supabase
                    .from("user_subscription")
                    .select(`*`)
                    .eq("user_id", user_id)
                    .limit(1)
                    .single();

            if (user_subscription_error) {
                console.error(
                    "Error fetching subscription status:",
                    user_subscription_error,
                );
                return { isSubscribed: false };
            }

            if (!user_subscription) {
                return { isSubscribed: false };
            }

            const { data: subscription_tier, error: subscription_tier_error } =
                await supabase
                    .from("subscription_tier")
                    .select(`*`)
                    .eq("tier_id", user_subscription.tier_id)
                    .limit(1)
                    .single();

            if (subscription_tier_error) {
                console.error(
                    "Error fetching subscription tier:",
                    subscription_tier_error,
                );
                return { isSubscribed: false };
            }

            const tier_code = subscription_tier.tier_code as SubscriptionTier;
            const isSubscribed = tier_code === "Registered" ? false : true;
            const subscription_id = user_subscription.stripe_sub_id;
            const customer_id = user_subscription.stripe_cust_id;
            const marked_for_cancel = user_subscription.marked_for_cancel;
            const cancel_at = user_subscription.cancel_at;

            return {
                isSubscribed,
                tier_code,
                subscription_id,
                customer_id,
                marked_for_cancel,
                cancel_at,
            };
        } catch (error) {
            console.error("Error fetching subscription status:", error);
            return { isSubscribed: false };
        }
    }

    async undoCancelSubscription(
        user_id: string,
    ): Promise<SubscriptionResponse> {
        try {
            const { data, error } = await supabase.functions.invoke(
                "undo-cancel-subscription",
                { method: "PUT", body: { user_id } },
            );

            if (error) {
                console.error(
                    "Error undoing subscription cancellation:",
                    error,
                );
                return {
                    success: false,
                    message: "Could not undo subscription cancellation.",
                    error,
                };
            }

            return {
                success: true,
                message: "Subscription cancellation undone successfully",
            };
        } catch (error) {
            console.error("Error undoing subscription cancellation:", error);
            return {
                success: false,
                message: "An unexpected error occurred",
                error,
            };
        }
    }
}

export const subscriptionService = SubscriptionService.getInstance();
